import React, { useState } from 'react';
import { useFormik } from 'formik';
import { colorPalette, theme, Lang, KNOWLEDGE_RESOURCES_TYPES } from 'shared/constants';
import styles from './communication-compose.module.scss';
import { ConfigProvider, Modal, notification, Upload } from 'antd';
import { Divider, Button, TextInput, TextAreaInput } from 'src/components/design-system';
import { RecipientsGroupSelector } from '../recipients-group-selector';
import { faPaperPlaneTop } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faPaperclip } from '@fortawesome/pro-solid-svg-icons';
import { SelectContent } from 'src/components/core/select-content';
import { ALLOWED_MIME_TYPES } from 'shared/constants/assets.constants';
import { ResourceList } from 'src/components/core/select-content/resource-list.component';
import { useKnowledgeParser } from 'shared/utils/rotations';
import { IndividualsSelector } from '../recipients-group-selector/individuals-selector';
import { TotalRecipientsProvider } from '../recipients-group-selector/total-recipients.context';
import { MESSAGE_SCHEMA, OPTIONS_DEFAULTS, TITLE_MAX_LENGTH } from './communication-compose.schema';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants';
import { NotificationOptionsToggles } from '../notification-options-toggles/notification-options-toggles';

export function CommunicationCompose({
  sendNotification,
  lang = Lang.COMMUNICATION_CENTER,
  uploadFile,
  userId,
  knowledge,
  admission,
  lab,
}) {
  const [isLoading, setLoadingState] = useState(false);
  const [formSendDate, setFormSendDate] = useState(Date.now());
  const [isSelectContentOpen, setSelectContentOpen] = useState(false);
  const [isLoadingFile, setLoadingFile] = useState(false);
  const [api, contextHolder] = notification.useNotification({ placement: 'bottomRight' });

  const addAttachment = payload => {
    if (
      payload.type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL &&
      values.attachments.some(attachment => attachment.url === payload.url)
    ) {
      return;
    }
    setFieldValue('attachments', [...values.attachments, { ...payload, createdBy: userId }]);
  };

  const removeAttachment = ({ type, id, url, ...rest }) => {
    setFieldValue(
      'attachments',
      // if the attachment is a external url, remove it by url, otherwise remove it by id
      values.attachments.filter(a => {
        if (
          a.type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL &&
          type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL
        ) {
          return a.url !== url;
        }
        return a.id !== id;
      }),
    );
  };

  const handleUploadFile = async ({ file }) => {
    try {
      setLoadingFile(true);
      const res = await uploadFile({ file });

      if (res.error) {
        api.error({
          title: res.error,
        });
        return;
      }
      addAttachment({
        url: res.url,
        title: file.name,
        type: KNOWLEDGE_RESOURCES_TYPES.FILE,
      });

      setLoadingFile(false);
    } catch (err) {
      api.error({
        title: err.message,
      });
    }
  };

  const onSubmit = async (data, { resetForm }) => {
    setLoadingState(true);

    try {
      const res = await sendNotification({ data });
      analyticsService.track(EVENTS.COMMUNICATION_CENTER.MESSAGE_SENT, {
        communicationId: res.data.message.id,
      });
      if (res.error) {
        api.error({
          title: lang.NOTIFICATION_SENT_ERROR,
        });
        return;
      }

      // Clean form on success
      resetForm();
      setFormSendDate(Date.now());
      api.success({
        title: lang.NOTIFICATION_SENT_SUCCESS,
        placement: 'bottomRight',
      });
    } catch (err) {
      api.error({
        title: err.message,
      });
    } finally {
      setLoadingState(false);
    }
  };

  const handleSelectContent = async payload => {
    addAttachment(payload);
    setSelectContentOpen(false);
  };

  const { handleChange, handleSubmit, values, errors, setFieldValue, isValid, dirty } = useFormik({
    validationSchema: MESSAGE_SCHEMA,
    enableReinitialize: true,
    initialValues: {
      options: OPTIONS_DEFAULTS,
      recipientsGroups: [{}], // Set one empty group by default
      individuals: [],
      attachments: [],
    },
    onSubmit,
  });

  const { parse } = useKnowledgeParser({
    knowledge,
    admission,
    lab,
    resources: values.attachments,
  });

  const setOptionsCheckboxState = key => checked => {
    setFieldValue(`options.${key}`, checked);
  };

  return (
    <>
      {contextHolder}
      <div className={styles.root} key={formSendDate}>
        <form onSubmit={handleSubmit}>
          <TotalRecipientsProvider
            individuals={values.individuals}
            groups={values.recipientsGroups}>
            <div className={styles.multiInput}>
              <RecipientsGroupSelector
                groups={values.recipientsGroups}
                name="recipientsGroups"
                onChange={setFieldValue}
                error={errors.recipientsGroups}
              />

              <Divider dashed />

              <IndividualsSelector
                value={values.individuals}
                name="individuals"
                error={errors.individuals}
                onChange={setFieldValue}
              />

              <Divider dashed />

              <TextInput
                name="title"
                className={styles.title}
                value={values.title}
                error={errors.title}
                onChange={handleChange}
                placeholder={lang.TITLE_PLACEHOLDER}
                variant="borderless"
                size="large"
                maxLength={TITLE_MAX_LENGTH}
                required
              />

              <Divider />

              <TextAreaInput
                name="content"
                value={values.content}
                error={errors.content}
                className={styles.content}
                onChange={handleChange}
                placeholder={lang.CONTENT_PLACEHOLDER}
                variant="borderless"
                size="medium"
                rows={10}
                style={{ resize: 'none' }}
                required
              />

              <ResourceList
                resources={values.attachments.map(parse)}
                canEdit
                backgroundColor={colorPalette.neutral2}
                doRemove={removeAttachment}
                showNotFoundPanda={false}
                wrapperClass={styles.resourceItem}
                openInNewTab
              />

              <Divider className={styles.footerDivider} />

              <div className={styles.footer}>
                <div className={styles.buttonWrapper}>
                  <Button
                    className={styles.button}
                    size="medium"
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    disabled={!isValid || isLoadingFile || !dirty}>
                    <FontAwesomeIcon icon={faPaperPlaneTop} className={styles.sendIcon} />
                    {lang.SEND}
                  </Button>
                  <Button
                    className={styles.button}
                    size="medium"
                    type="default"
                    disabled={isLoading}
                    onClick={() => setSelectContentOpen(true)}>
                    <FontAwesomeIcon icon={faLink} className={styles.sendIcon} />
                    {lang.ADD_RESOURCE}
                  </Button>
                  {
                    // TODO: see antd Upload and Dragger, might be useful to implement drag and drop functionality
                  }
                  <Upload
                    showUploadList={false}
                    accept={Object.values(ALLOWED_MIME_TYPES).join(',')}
                    customRequest={handleUploadFile}>
                    <Button
                      className={styles.button}
                      size="medium"
                      type="default"
                      loading={isLoadingFile}
                      disabled={isLoading}>
                      <FontAwesomeIcon icon={faPaperclip} className={styles.sendIcon} />
                      {lang.ATTACH}
                    </Button>
                  </Upload>
                </div>
                <div className={styles.toggles}>
                  <NotificationOptionsToggles
                    setOptionsCheckboxState={setOptionsCheckboxState}
                    values={values}
                  />
                </div>
              </div>
              <input type="file" style={{ display: 'none' }} onChange={() => {}} />
            </div>
          </TotalRecipientsProvider>
        </form>
        <ConfigProvider
          theme={{
            components: {
              Modal: {
                contentBg: colorPalette.neutral2,
                headerBg: colorPalette.neutral2,
                fontFamily: theme.newFontFamily,
                fontSizeHeading2: theme.modalHeaderFontSize,
              },
            },
          }}>
          <Modal
            open={isSelectContentOpen}
            footer={null}
            width={1000}
            centered
            mask={{ closable: false }}
            destroyOnHidden
            closable={false}
            title={<h2>{lang.ADD_RESOURCE}</h2>}>
            <SelectContent
              disabledIds={values.attachments.map(a => a.id)}
              onCancel={() => {
                setSelectContentOpen(false);
              }}
              onSubmit={handleSelectContent}
            />
          </Modal>
        </ConfigProvider>
      </div>
    </>
  );
}
